import { FC, useEffect, useState } from "react";
import { classes, stylesheet } from "typestyle";
import { IActivityEntry } from "@hulanbv/platformapp";
import { Flex } from "../../../elements/flex.element";
import { Button } from "../../../elements/button.element";
import { Card } from "../../../elements/card.element";

type Props = {
  values: IActivityEntry[];
  onComplete: () => void;
};

const TwoSidesOfTheSameCoinOverviewView: FC<Props> = ({
  values,
  onComplete,
}) => {
  const [cardNumber, setCardNumber] = useState(0);

  // scroll to top when cardNumber changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [cardNumber]);

  return (
    <Flex
      flex={1}
      justifyContent="center"
      alignItems="stretch"
      direction="column"
      attributes={{ style: { marginBottom: 50 } }}
    >
      <Flex
        direction="column"
        justifyContent="center"
        alignItems="stretch"
        gap={50}
      >
        <p className={styles.info}>
          Take a look at the barriers on your values cards and see what kinds of
          thoughts and feelings come up when you look at them? How long have you
          held onto them?
        </p>
        <p className={styles.info}>
          If you didn't care about the value so much, the barrier wouldn't hurt
          so much. You can't get rid of the pain entirely without getting rid of
          the value.
        </p>
        <p className={styles.info}>
          Acceptance means carrying these barriers so you can live your values.
        </p>
        <Flex
          flex={1}
          direction="column"
          justifyContent="stretch"
          attributes={{ className: styles.cardContainer }}
        >
          {values?.map((component, index) => {
            const position = index - cardNumber;
            return (
              <Card
                key={`${component.id}-${index}`}
                attributes={{
                  className: classes(
                    styles.card,
                    cardNumber > index && styles.discarded,
                  ),
                  style: {
                    zIndex: values.length - position,
                    transform: `translateY(${position * 15}px) scale(${
                      1 - position * 0.05
                    })`,
                    opacity: position < 0 ? 0 : 1.33 - position * 0.33,
                    pointerEvents: index === cardNumber ? "auto" : "none",
                    transition:
                      "transform 0.5s, opacity 1s, translate 0.5s, rotate 0.5s",
                  },
                }}
              >
                <Flex
                  direction="column"
                  alignItems="stretch"
                  flex={1}
                  padding={20}
                >
                  <h3 style={{ margin: 0 }}>{component.input}</h3>
                  <p style={{ margin: 0, overflowY: "auto" }}>
                    {component.value}
                  </p>
                </Flex>
              </Card>
            );
          })}
        </Flex>
      </Flex>
      <Flex direction="row" justifyContent="flex-end" flex={0}>
        <Button
          flavour="ghost-primary"
          hideSpinnerOnSubmit
          centerChildren
          attributes={{
            style: { visibility: cardNumber === 0 ? "hidden" : "visible" },
            onClick: () => {
              if (cardNumber > 0) {
                setCardNumber(cardNumber - 1);
              }
            },
          }}
        >
          Back
        </Button>
        <Button
          hideSpinnerOnSubmit
          centerChildren
          attributes={{
            onClick: () => {
              if (cardNumber < values.length - 1) {
                setCardNumber(cardNumber + 1);
              } else {
                onComplete();
              }
            },
          }}
        >
          Next
        </Button>
      </Flex>
    </Flex>
  );
};

export { TwoSidesOfTheSameCoinOverviewView };

const styles = stylesheet({
  cardContainer: {
    position: "relative",
    minHeight:
      "min(500px, calc(80vh - var(--nav-bar-height) + env(safe-area-inset-top)))",
  },
  info: {
    fontSize: ".9em",
    color: "rgb(var(--rgb-color-text-tertiary))",
    textAlign: "center",
    margin: 0,
  },

  card: {
    minHeight: "90%",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "stretch",
    position: "absolute",
    translate: "0 0",
    maxHeight: "500px",
  },

  discarded: {
    translate: "-100% 0 !important",
    rotate: "-20deg",
    transformOrigin: "left bottom",
  },
});
